var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { getLOBName, getUnassignedLOB, sortByLOBName } from '../../common';
import { useFormlessContext } from '../../hooks';
import { useMultiskillLocations } from '../../multiskill/multiskillGroup/multiskillLocationsContext';
import { TableComponent } from '../common/tableComponent';
import { LocationTableEntryMode } from '../constants';
import { LocationTable } from '../locationTable';
import { useQueueAPI } from '../queue/selectors';
import { Completed, QueueStatusType } from '../queue/types';
import { useEffectiveQueueLocations, useWizardParams } from '../selectors';
var checkAllLearnCurves100 = function (volumes) {
    if (!(volumes === null || volumes === void 0 ? void 0 : volumes.length))
        return true;
    return volumes.every(function (v) { return Number(v === null || v === void 0 ? void 0 : v.learnCurve) === 100 || (v === null || v === void 0 ? void 0 : v.learnCurve) == null; });
};
var useIsLearningCurve100 = function () {
    var _a, _b, _c;
    var queueData = useQueueAPI()[0];
    var queueLocations = useEffectiveQueueLocations();
    var hasQueueLocations = !!queueLocations.length;
    var formVolumes = useWatch({ name: 'volumes' });
    var formQueueLocations = useWatch({ name: 'queueLocations' });
    var multiskillLearningCurve100 = (_a = useMultiskillLocations()) === null || _a === void 0 ? void 0 : _a.learningCurve100;
    return useMemo(function () {
        var _a, _b, _c, _d, _e;
        // The multiskill group has no learning-curve inputs of its own; the group
        // form provides a backend-computed status derived from all member queues.
        if (multiskillLearningCurve100 != null)
            return multiskillLearningCurve100;
        if (hasQueueLocations) {
            // Check per-location volumes (multi-geo)
            var hasFormLocationLearnCurve = formQueueLocations === null || formQueueLocations === void 0 ? void 0 : formQueueLocations.some(function (loc) { var _a; return (_a = loc === null || loc === void 0 ? void 0 : loc.volumes) === null || _a === void 0 ? void 0 : _a.some(function (v) { return (v === null || v === void 0 ? void 0 : v.learnCurve) != null; }); });
            var locationSources = hasFormLocationLearnCurve ? formQueueLocations : (_b = (_a = queueData.data) === null || _a === void 0 ? void 0 : _a.queueLocations) !== null && _b !== void 0 ? _b : [];
            for (var _i = 0, locationSources_1 = locationSources; _i < locationSources_1.length; _i++) {
                var loc = locationSources_1[_i];
                if (!checkAllLearnCurves100((_c = loc === null || loc === void 0 ? void 0 : loc.volumes) !== null && _c !== void 0 ? _c : []))
                    return false;
            }
        }
        else {
            // Check network-level volumes
            var hasFormLearnCurve = formVolumes === null || formVolumes === void 0 ? void 0 : formVolumes.some(function (v) { return (v === null || v === void 0 ? void 0 : v.learnCurve) != null; });
            var networkOk = hasFormLearnCurve
                ? checkAllLearnCurves100(formVolumes)
                : checkAllLearnCurves100((_e = (_d = queueData.data) === null || _d === void 0 ? void 0 : _d.volumes) !== null && _e !== void 0 ? _e : []);
            if (!networkOk)
                return false;
        }
        return true;
    }, [
        formQueueLocations,
        formVolumes,
        hasQueueLocations,
        multiskillLearningCurve100,
        (_b = queueData.data) === null || _b === void 0 ? void 0 : _b.queueLocations,
        (_c = queueData.data) === null || _c === void 0 ? void 0 : _c.volumes,
    ]);
};
export var AttritionContent = function () {
    var _a;
    var control = ((_a = useFormlessContext()) !== null && _a !== void 0 ? _a : {}).control;
    var _b = useFormContext(), setValue = _b.setValue, getValues = _b.getValues;
    var entryMode = useWatch({ name: 'attritionEntryMode', control: control });
    var isPercentage = entryMode !== LocationTableEntryMode.Absolute;
    var config = isPercentage ? { decimal: 2, min: 0, max: 99.99, suffix: '%' } : { decimal: 2, min: 0, max: 9999 };
    var isLearningCurve100 = useIsLearningCurve100();
    var isAbsoluteDisabled = !isLearningCurve100;
    var locations = useEffectiveQueueLocations();
    var hasQueueLocations = !!locations.length;
    // Absolute mode requires all learning curves to be 100%;
    // if that invariant is violated, reset to percentage and clear attrition.
    useEffect(function () {
        if (entryMode === LocationTableEntryMode.Absolute && !isLearningCurve100) {
            if (hasQueueLocations) {
                var queueLocations = getValues('queueLocations');
                if (queueLocations) {
                    queueLocations.forEach(function (loc, locIdx) {
                        var _a;
                        (_a = loc === null || loc === void 0 ? void 0 : loc.volumes) === null || _a === void 0 ? void 0 : _a.forEach(function (_, idx) {
                            setValue("queueLocations.".concat(locIdx, ".volumes.").concat(idx, ".attrition"), null, { shouldDirty: true });
                        });
                    });
                }
            }
            else {
                var volumes = getValues('volumes');
                if (volumes) {
                    volumes.forEach(function (_, index) {
                        setValue("volumes.".concat(index, ".attrition"), null, { shouldDirty: true });
                    });
                }
            }
            setValue('attritionEntryMode', 'percentage', { shouldDirty: true });
        }
    }, [entryMode, isLearningCurve100, getValues, setValue, hasQueueLocations]);
    return (_jsx(LocationTable, __assign({ addColumns: ['Total', 'Average'], name: "attrition", entryModeField: "attritionEntryMode", isAbsoluteDisabled: isAbsoluteDisabled, absoluteDecimal: 2 }, config, { showGroupHeader: true })));
};
export var AttritionStep = function () {
    var queueId = useWizardParams().queueId;
    var isLearningCurve100 = useIsLearningCurve100();
    var isAbsoluteDisabled = !isLearningCurve100;
    var getAttritionCopyOptions = useCallback(function (queues) {
        var unassigned = getUnassignedLOB();
        return queues
            .filter(function (_a) {
            var id = _a.id, status = _a.status, attritionEntryMode = _a.attritionEntryMode;
            return id !== queueId &&
                status[QueueStatusType.Attrition] !== Completed.No &&
                (!isAbsoluteDisabled || attritionEntryMode !== LocationTableEntryMode.Absolute);
        })
            .map(function (_a) {
            var id = _a.id, name = _a.name, lobName = _a.lobName, subLobName = _a.subLobName;
            return ({
                id: id.toString(),
                name: name,
                lobName: getLOBName(lobName, subLobName, unassigned),
            });
        })
            .sort(function (a, b) { return sortByLOBName(a, b, unassigned); });
    }, [queueId, isAbsoluteDisabled]);
    return (_jsx(TableComponent, __assign({ name: "attrition", statusType: QueueStatusType.Attrition, needSaveBeforeCopy: true, getOptions: getAttritionCopyOptions }, { children: _jsx(AttritionContent, {}) })));
};
